import { ChangeDetectionStrategy, Component, computed, inject, Signal, signal, WritableSignal } from '@angular/core';
import {
  OrAvatarComponent,
  OrIconComponent,
  PageActionsComponent,
  PageLayoutComponent,
  TitileNavbarComponent
} from '@ui-components';
import { LocalRefreshService, MODAL_TOKEN, ModalProvider } from '@core';
import { ActivatedRoute, Params } from '@angular/router';
import { IonicModule } from '@ionic/angular';
import { map, switchMap, take, tap } from 'rxjs';
import {
  AddTeamMemberCommandHandler,
  GetTeamCandidatesQueryHandler,
  GetTeamCandidatesQueryResult,
  TeamCandidate
} from '../../application/handlers';
import { TeamInviteData } from '../../application/models';
import { ShareTeamModalComponent } from '../share-team-modal/share-team-modal.component';
import { toSignal } from '@angular/core/rxjs-interop';

type TeamPlayerViewModel = TeamCandidate & { isSelected: boolean };

@Component({
  selector: 'lib-team-invite-players',
  templateUrl: './team-invite-players.component.html',
  styleUrls: ['./team-invite-players.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    TitileNavbarComponent,
    IonicModule,
    OrAvatarComponent,
    PageLayoutComponent,
    PageActionsComponent,
    OrIconComponent
  ],
  providers: [GetTeamCandidatesQueryHandler, LocalRefreshService, AddTeamMemberCommandHandler]
})
export class TeamInvitePlayersComponent {
  private readonly getTeamCandidatesQueryHandler: GetTeamCandidatesQueryHandler = inject(GetTeamCandidatesQueryHandler);
  private readonly addTeamMemberCommandHandler: AddTeamMemberCommandHandler = inject(AddTeamMemberCommandHandler);
  private readonly localRefreshService: LocalRefreshService = inject(LocalRefreshService);
  private readonly activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private readonly modalProvider: ModalProvider = inject(MODAL_TOKEN);

  readonly selectedUsers: WritableSignal<string[]> = signal<string[]>([]);

  readonly teamData: Signal<TeamInviteData | undefined> = toSignal(
    this.activatedRoute.params.pipe(
      map((params: Params) => ({
        teamId: params['id'],
        token: params['token']
      }))
    )
  );

  readonly candidates: Signal<TeamPlayerViewModel[] | undefined> = toSignal(
    this.localRefreshService.refresh$.pipe(
      switchMap(() => this.modalProvider.showLoading$()),
      switchMap(() => this.activatedRoute.params),
      map((params: Params) => params['id']),
      switchMap((teamId: string) => this.getTeamCandidatesQueryHandler.candidates(teamId)),
      map((result: GetTeamCandidatesQueryResult) =>
        result.availableToInvite.map((candidate: TeamCandidate) => ({
          ...candidate,
          isSelected: false
        }))
      ),
      tap(() => this.modalProvider.dismissLoading())
    )
  );

  readonly hasSelectedUsers: Signal<boolean> = computed(() => this.selectedUsers().length > 0);

  selectAll(event: CustomEvent): void {
    const eventValue: boolean = event.detail.checked;
    const currentCandidates: TeamPlayerViewModel[] | undefined = this.candidates();

    if (!currentCandidates) return;

    const payload: string[] = eventValue ? currentCandidates.map((player: TeamPlayerViewModel) => player.userId) : [];
    this.selectedUsers.set(payload);
  }

  selectPlayer(userId: string): void {
    const current: string[] = this.selectedUsers();

    if (current.includes(userId)) {
      this.selectedUsers.set(current.filter((id: string) => id !== userId));
    } else {
      this.selectedUsers.set([...current, userId]);
    }
  }

  isSelected(memberId: string): boolean {
    return this.selectedUsers().includes(memberId);
  }

  inviteUsers(): void {
    const data: TeamInviteData | undefined = this.teamData();
    const users: string[] = this.selectedUsers();

    if (!data || users.length === 0) return;

    this.addTeamMemberCommandHandler
      .add(data.teamId, users)
      .pipe(take(1))
      .subscribe(() => {
        this.localRefreshService.emit();
        this.selectedUsers.set([]);
      });
  }

  inviteViaLink(): void {
    const data: TeamInviteData | undefined = this.teamData();
    if (!data) return;

    this.modalProvider.showModal$({
      component: ShareTeamModalComponent,
      componentProps: { teamData: data },
      cssClass: 'modal-auto',
      initialBreakpoint: 1,
      breakpoints: [0, 1]
    });
  }
}
